import { useAuth } from '../context/AuthContext'
import { useGame } from '../context/GameContext'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'
import { useToast } from '../components/ui/Toaster'
import { 
  Share2, ArrowRight, Trophy, Target, 
  Sparkles, Heart, MessageSquare,
  TrendingUp, Flame
} from 'lucide-react'
import { clsx } from 'clsx'
import { useState, useEffect } from 'react'

export default function DuelResult() {
  const { inviteCode } = useParams<{ inviteCode: string }>()
  const { user } = useAuth()
  const { currentDuel, fetchDuel, createDuel } = useGame()
  const { toast } = useToast()
  const navigate = useNavigate()

  const [showConfetti, setShowConfetti] = useState(false)

  useEffect(() => {
    if (inviteCode) {
      fetchDuel(inviteCode)
    }
  }, [inviteCode, fetchDuel])

  useEffect(() => {
    if (currentDuel?.status === 'completed') {
      setShowConfetti(true)
      setTimeout(() => setShowConfetti(false), 3000)
    }
  }, [currentDuel])

  const isCreator = user && currentDuel?.creator_id === user.id

    const creatorA = currentDuel?.creator_spectrum_a ?? 50
    const creatorB = currentDuel?.creator_spectrum_b ?? 50
    const opponentA = currentDuel?.opponent_spectrum_a ?? 50
    const opponentB = currentDuel?.opponent_spectrum_b ?? 50

    const myA = isCreator ? creatorA : opponentA
    const myB = isCreator ? creatorB : opponentB
    const theirA = isCreator ? opponentA : creatorA
    const theirB = isCreator ? opponentB : creatorB

    const syncA = 100 - Math.abs(myA - theirA)
    const syncB = 100 - Math.abs(myB - theirB)
    const syncPercentage = Math.round((syncA + syncB) / 2)

    const syncLevel = syncPercentage >= 80 ? 'Soulmates' : syncPercentage >= 60 ? 'In Sync' : syncPercentage >= 40 ? 'Mixed Signals' : 'Opposites'
    const syncIcon = syncPercentage >= 80 ? <Heart className="w-6 h-6" /> : syncPercentage >= 60 ? <Target className="w-6 h-6" /> : syncPercentage >= 40 ? <Sparkles className="w-6 h-6" /> : <TrendingUp className="w-6 h-6" />
    const syncColor = syncPercentage >= 80 ? 'text-pink-500' : syncPercentage >= 60 ? 'text-brand-500' : syncPercentage >= 40 ? 'text-purple-500' : 'text-blue-500'

    const prompt = currentDuel?.prompt
    const creatorUsername = currentDuel?.creator_username || 'Creator'
    const opponentUsername = currentDuel?.opponent_username || 'Opponent'

    const shareUrl = `${window.location.origin}/duel/${inviteCode}/result`
    const [copied, setCopied] = useState(false)

    const copyShareLink = async () => {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      toast({ type: 'success', title: 'Link copied!' })
    }

    const handleRematch = async () => {
      if (!prompt) return
      const { inviteCode: newCode, error } = await createDuel(prompt.id)
      if (error) {
        toast({ type: 'error', title: 'Failed', message: error.message })
      } else if (newCode) {
        navigate(`/duel/${newCode}`)
      }
    }

    if (!currentDuel) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-950 px-4">
          <Card className="max-w-md w-full text-center p-8">
            <div className="w-12 h-12 mx-auto mb-4 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-surface-600 dark:text-surface-400">Loading results...</p>
          </Card>
        </div>
      )
    }

    if (currentDuel.status !== 'completed') {
      return (
        <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-950 px-4">
          <Card className="max-w-md w-full text-center p-8">
            <div className="w-12 h-12 mx-auto mb-4 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-surface-600 dark:text-surface-400">Waiting for both players to answer...</p>
          </Card>
        </div>
      )
    }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 lg:py-10 space-y-6">
      {/* Confetti */}
      {showConfetti && <Confetti />}

      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-brand-500 to-brand-600 rounded-full text-white font-semibold mb-4">
          <Trophy className="w-5 h-5" />
          <span>Duel Complete</span>
        </div>
        <h1 className="text-3xl font-bold text-surface-900 dark:text-surface-100 mb-2">
          {prompt?.spectrum_a_label || 'Spectrum A'} + {prompt?.spectrum_b_label || 'Spectrum B'}
        </h1>
        <p className="text-surface-500 dark:text-surface-400">Code: {currentDuel.invite_code}</p>
      </div>

      {/* Sync Score - Main Card */}
      <Card className="bg-gradient-to-br from-brand-50 to-brand-100 dark:from-brand-900/30 dark:to-brand-800/30 border-brand-200 dark:border-brand-800 overflow-hidden">
        <CardContent className="pt-8 pb-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-white dark:bg-surface-900 shadow-lg mb-4">
              <div className={clsx('text-5xl font-black', syncColor)}>{syncPercentage}%</div>
            </div>
            <h2 className="text-2xl font-bold text-brand-900 dark:text-brand-100 mb-1">Sync Score</h2>
            <div className="inline-flex items-center gap-2 px-4 py-1 rounded-full bg-white/50 dark:bg-surface-900/50 backdrop-blur">
              {syncIcon}
              <span className={clsx('font-semibold text-lg', syncColor)}>{syncLevel}</span>
            </div>
          </div>

          {/* Breakdown */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <SyncBreakdown
              label={prompt?.spectrum_a_label || 'Spectrum A'}
              myValue={myA}
              theirValue={theirA}
              sync={syncA}
              left={prompt?.spectrum_a_left || 'Left'}
              right={prompt?.spectrum_a_right || 'Right'}
              color="brand"
            />
            <SyncBreakdown
              label={prompt?.spectrum_b_label || 'Spectrum B'}
              myValue={myB}
              theirValue={theirB}
              sync={syncB}
              left={prompt?.spectrum_b_left || 'Left'}
              right={prompt?.spectrum_b_right || 'Right'}
              color="purple"
            />
          </div>

          {/* Player values */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-xs text-surface-500 uppercase tracking-wide">You</p>
              <p className="text-2xl font-bold text-surface-900 dark:text-surface-100">{myA} / {myB}</p>
            </div>
            <div className="flex items-center justify-center">
              <div className="w-16 h-1 bg-surface-200 dark:bg-surface-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-brand-500 to-purple-500 rounded-full"
                  style={{ width: `${syncPercentage}%` }}
                />
              </div>
            </div>
            <div>
              <p className="text-xs text-surface-500 uppercase tracking-wide">{isCreator ? opponentUsername : creatorUsername}</p>
              <p className="text-2xl font-bold text-surface-900 dark:text-surface-100">{theirA} / {theirB}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Breakdown */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="font-semibold text-surface-900 dark:text-surface-100 mb-4">How You Aligned</h3>
          
          <div className="space-y-4">
            <AlignmentBar
              label={prompt?.spectrum_a_label || 'Spectrum A'}
              left={prompt?.spectrum_a_left || 'Left'}
              right={prompt?.spectrum_a_right || 'Right'}
              myValue={myA}
              theirValue={theirA}
              crowdAvg={currentDuel.crowd_a_avg ?? prompt?.crowd_a_avg ?? 50}
              color="brand"
            />
            <AlignmentBar
              label={prompt?.spectrum_b_label || 'Spectrum B'}
              left={prompt?.spectrum_b_left || 'Left'}
              right={prompt?.spectrum_b_right || 'Right'}
              myValue={myB}
              theirValue={theirB}
              crowdAvg={currentDuel.crowd_b_avg ?? prompt?.crowd_b_avg ?? 50}
              color="purple"
            />
          </div>
        </CardContent>
      </Card>

      {/* Share Card Preview */}
      <Card className="bg-gradient-to-br from-brand-50 to-brand-100 dark:from-brand-900/30 dark:to-brand-800/30 border-brand-200 dark:border-brand-800">
        <CardContent className="pt-6">
          <p className="text-sm font-medium text-brand-700 dark:text-brand-300 mb-4 text-center">Your Share Card</p>
          <div className="bg-white dark:bg-surface-900 rounded-xl p-6 text-center">
            <div className="text-5xl font-black text-brand-600 dark:text-brand-400 mb-1">{syncPercentage}%</div>
            <div className="text-brand-700 dark:text-brand-300 font-semibold mb-2">{syncLevel}</div>
            <div className="flex items-center justify-center gap-2 text-sm text-surface-500 mb-4">
              <span>vs</span>
              <span className="font-semibold text-surface-900 dark:text-surface-100">{isCreator ? opponentUsername : creatorUsername}</span>
            </div>
            <div className="mt-4 pt-4 border-t border-surface-200 dark:border-surface-700 text-xs text-surface-400">
              Play at overlap.game
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="space-y-3">
        <Button className="w-full" size="lg" onClick={copyShareLink}>
          <Share2 className="w-5 h-5 mr-2" />
          {copied ? 'Copied!' : 'Copy Result Link'}
        </Button>
        <div className="grid grid-cols-2 gap-3">
          <Button variant="secondary" onClick={handleRematch}>
            <MessageSquare className="w-4 h-4 mr-2" />
            Rematch
          </Button>
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowRight className="w-4 h-4 mr-2" />
            Home
          </Button>
        </div>
        <Button variant="ghost" className="w-full" onClick={() => navigate('/daily')}>
          <Flame className="w-4 h-4 mr-2" />
          Play Daily Game
        </Button>
      </div>
    </div>
  )
}

interface SyncBreakdownProps {
  label: string
  myValue: number
  theirValue: number
  sync: number
  left: string
  right: string
  color: 'brand' | 'purple'
}

function SyncBreakdown({ label, myValue, theirValue, sync, left, right, color }: SyncBreakdownProps) {
  const colors = {
    brand: 'text-brand-600 dark:text-brand-400 bg-brand-500',
    purple: 'text-purple-600 dark:text-purple-400 bg-purple-500',
  }

  return (
    <div className="p-4 bg-surface-50 dark:bg-surface-900/50 rounded-xl">
      <p className="font-medium text-surface-900 dark:text-surface-100 mb-3">{label}</p>
      <div className="flex items-center justify-between text-sm mb-2">
        <span className="text-surface-500">{left}</span>
        <span className="text-surface-500">{right}</span>
      </div>
      <div className="h-3 bg-surface-200 dark:bg-surface-700 rounded-full overflow-hidden mb-3 relative">
        <div 
          className="absolute top-0 bottom-0 bg-gray-300 dark:bg-gray-600"
          style={{ left: `${Math.min(myValue, theirValue)}%`, width: `${Math.abs(myValue - theirValue)}%` }}
        />
        <div 
          className="absolute top-0 bottom-0 rounded-full"
          style={{ left: `${myValue}%`, width: '12px', marginLeft: '-6px', backgroundColor: colors[color] }}
        />
        <div 
          className="absolute top-0 bottom-0 rounded-full border-2 border-white"
          style={{ left: `${theirValue}%`, width: '12px', marginLeft: '-6px', backgroundColor: colors[color] }}
        />
      </div>
      <div className="flex justify-between text-xs text-surface-500">
        <span>You: {myValue}</span>
        <span className="font-semibold text-brand-600 dark:text-brand-400">{sync}% match</span>
        <span>Them: {theirValue}</span>
      </div>
    </div>
  )
}

interface AlignmentBarProps {
  label: string
  left: string
  right: string
  myValue: number
  theirValue: number
  crowdAvg: number
  color: 'brand' | 'purple'
}

function AlignmentBar({ label, left, right, myValue, theirValue, crowdAvg, color }: AlignmentBarProps) {
  const colors = {
    brand: 'bg-brand-500',
    purple: 'bg-purple-500',
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="font-medium text-surface-900 dark:text-surface-100">{label}</span>
        <span className="text-sm text-surface-500">Crowd: {crowdAvg}</span>
      </div>
      <div className="relative h-4">
        <div className="absolute inset-0 bg-surface-200 dark:bg-surface-700 rounded-full" />
        <div 
          className="absolute top-0 bottom-0 rounded-full"
          style={{ left: `${crowdAvg}%`, width: '4px', backgroundColor: '#fff', boxShadow: '0 0 0 2px currentColor' }}
        />
        <div 
          className="absolute top-0 bottom-0 rounded-full"
          style={{ left: `${myValue}%`, width: '16px', marginLeft: '-8px', backgroundColor: colors[color] }}
        />
        <div 
          className="absolute top-0 bottom-0 rounded-full border-2 border-white"
          style={{ left: `${theirValue}%`, width: '16px', marginLeft: '-8px', backgroundColor: colors[color] }}
        />
      </div>
      <div className="flex justify-between text-xs text-surface-500">
        <span>{left}</span>
        <span>{right}</span>
      </div>
    </div>
  )
}

function Confetti() {
  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {[...Array(50)].map((_, i) => (
        <ConfettiPiece key={i} index={i} />
      ))}
    </div>
  )
}

function ConfettiPiece({ index }: { index: number }) {
  const colors = ['#F59E0B', '#EF4444', '#8B5CF6', '#3B82F6', '#10B981', '#EC4899', '#F97316']
  const color = colors[index % colors.length]
  const left = Math.random() * 100
  const delay = Math.random() * 1
  const duration = 2 + Math.random() * 2
  const size = 6 + Math.random() * 8

  return (
    <div
      className="absolute"
      style={{
        left: `${left}%`,
        top: '-20px',
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: color,
        borderRadius: Math.random() > 0.5 ? '50%' : '0',
        animation: `confetti-fall ${duration}s ease-in ${delay}s forwards`,
        transform: `rotate(${Math.random() * 360}deg)`,
      }}
    />
  )
}

// Add confetti animation
if (typeof document !== 'undefined' && !document.getElementById('confetti-style')) {
  const style = document.createElement('style')
  style.id = 'confetti-style'
  style.textContent = `
    @keyframes confetti-fall {
      0% { transform: translateY(0) rotate(0deg); opacity: 1; }
      100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
    }
  `
  document.head.appendChild(style)
}